import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaArrowUp,
  FaBus,
  FaCheck,
  FaSearch,
  FaSyncAlt,
  FaUserGraduate,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import SelectSessionClass from "../ChildComponent/SelectSessionClass";
import BackButton from "../ChildComponent/BackButton";
import MdlBatchShift from "../dialog/MdlBatchShift";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/admission-dashboard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const SERVICE_ID = "SRV001";

//Created By Cursor
function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

//Created By Cursor
const StudentAvatar = memo(function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = Boolean(imagePath) && !imgFailed;

  return (
    <div className="rn-row__avatar" aria-hidden>
      {showImg ? (
        <img src={imagePath} alt="" onError={() => setImgFailed(true)} />
      ) : (
        <span className="rn-row__initials">{studentInitials(name)}</span>
      )}
    </div>
  );
});

//Created By Cursor
const StudentPromoteRow = memo(function StudentPromoteRow({
  student,
  selected,
  index,
  onToggle,
}) {
  const classLabel =
    student.section_id === "No"
      ? student.class_name
      : `${student.class_name || ""}:${student.section_name || ""}`;

  return (
    <div
      className={`rn-row rn-row--classchange${
        selected ? " rn-row--selected" : ""
      }`}
      style={{ "--rn-i": index }}
    >
      <div className="rn-row__who">
        <StudentAvatar
          name={student.stu_name}
          imagePath={student.student_img_path}
        />
        <div className="min-w-0">
          <div className="rn-row__name">{student.stu_name || "—"}</div>
          <div className="rn-row__chips">
            <span className="rn-chip rn-chip--father">
              {student.father_name ? `S/O ${student.father_name}` : "—"}
            </span>
            {classLabel ? (
              <span className="rn-chip rn-chip--class">{classLabel}</span>
            ) : null}
            {student.student_enrollment_no ? (
              <span className="rn-chip rn-chip--enroll">
                {student.student_enrollment_no}
              </span>
            ) : null}
            {student.student_roll_no ? (
              <span className="rn-chip rn-chip--enroll">
                Roll: {student.student_roll_no}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div
        className={`rn-row__field${selected ? " rn-row__field--selected" : ""}`}
      >
        <label
          className="rn-row__label"
          htmlFor={`pts-select-${student.student_enrollment_no || index}`}
        >
          Select
        </label>
        <button
          type="button"
          id={`pts-select-${student.student_enrollment_no || index}`}
          className={`cc-select-btn${selected ? " is-selected" : ""}`}
          onClick={() => onToggle(student)}
          aria-pressed={selected}
          title={selected ? "Deselect student" : "Select student"}
        >
          {selected ? (
            <span className="cc-select-btn__tick" aria-hidden>
              <FaCheck />
            </span>
          ) : null}
          <span>{selected ? "Selected" : "Select"}</span>
        </button>
      </div>
    </div>
  );
});

//Created By Cursor
function PromoteTransportStudent() {
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadClass, setLoadClass] = useState(false);
  const [previousSession, setPreviousSession] = useState(null);
  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState("-1");
  const [classSectionName, setClassSectionName] = useState("");
  const [students, setStudents] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  //Created By Cursor
  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    axios
      .get(`${API_BASE()}getPreviousSession.php`, {
        params: { school_code: SessionManager.getSchoolCode() },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK" && response.data?.length) {
          const sess = response.data[0];
          setPreviousSession(sess);
          setSessionId(sess.session_id || "");
          setLoadClass(true);
        } else {
          myToast.notify_warning(
            response.api_remark || "No previous session found."
          );
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("[PromoteTransportStudent] session error:", error);
      });
  }, []);

  //Created By Cursor
  const loadStudents = useCallback(
    async ({ soft = false } = {}) => {
      if (!classId || !sectionId || !previousSession) {
        setStudents([]);
        setSelectedMap({});
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const res = await axios.get(
          `${API_BASE()}getPromoteTransportStudent.php`,
          {
            params: {
              service_id: SERVICE_ID,
              school_code: schoolCode,
              class_id: classId,
              section_id: sectionId,
              session_id: previousSession.session_id,
            },
          }
        );
        const payload = res?.data;

        if (payload?.api_status === "OK" && Array.isArray(payload.data)) {
          setStudents(payload.data);
          setSelectedMap((prev) => {
            const next = {};
            payload.data.forEach((row) => {
              const id = row.student_enrollment_no;
              if (id && prev[id]) next[id] = row;
            });
            return next;
          });
        } else {
          setStudents([]);
          setSelectedMap({});
          if (payload?.api_remark) myToast.notify_warning(payload.api_remark);
        }
      } catch (error) {
        console.error("[PromoteTransportStudent] load error", error);
        setStudents([]);
        setSelectedMap({});
        myToast.notify_warning("Failed to load transport students.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [classId, sectionId, previousSession]
  );

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  //Created By Cursor
  const onClassSelect = useCallback(
    (
      nextClassId,
      nextSectionId,
      _nextSessionId,
      position,
      className,
      sectionName
    ) => {
      setClassId(nextClassId);
      setSectionId(nextSectionId);
      setSelectedClassPosition(position);
      setClassSectionName(
        nextSectionId === "No" ? className : `${className}:${sectionName}`
      );
      setSelectedMap({});
      setSearchTerm("");
    },
    []
  );

  //Created By Cursor
  const toggleStudent = useCallback((student) => {
    const id = student.student_enrollment_no;
    if (!id) return;
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = student;
      return next;
    });
  }, []);

  const selectedStudents = useMemo(
    () => Object.values(selectedMap),
    [selectedMap]
  );
  const selectedCount = selectedStudents.length;

  const filteredStudents = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const hay = [
        s.stu_name,
        s.father_name,
        s.student_enrollment_no,
        s.student_roll_no,
        s.class_name,
        s.section_name,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [students, searchTerm]);

  //Created By Cursor
  const getStudentIds = useCallback(
    () =>
      JSON.stringify(
        selectedStudents.map(
          ({ student_enrollment_no, student_roll_no }) => ({
            student_enrollment_no,
            student_roll_no,
          })
        )
      ),
    [selectedStudents]
  );

  //Created By Cursor
  const promoteStudents = useCallback(
    async (newClassId, newSectionId, newSessionId) => {
      if (!selectedStudents.length) {
        myToast.notify_error("Select Student !!!");
        return;
      }

      setSubmitting(true);
      try {
        const params = new URLSearchParams();
        params.append("student_ids", getStudentIds());
        params.append("class_id", newClassId);
        params.append("section_id", newSectionId);
        params.append("old_session_id", sessionId);
        params.append("new_session_id", newSessionId);
        params.append("service_id", SERVICE_ID);
        params.append("school_code", SessionManager.getSchoolCode());

        const res = await axios.post(
          `${API_BASE()}promoteTransportStudent.php`,
          params
        );
        const payload = res?.data;

        if (payload?.api_status === "OK") {
          myToast.notify_success("Promoted Successfully!!!");
          setSelectedMap({});
          await loadStudents({ soft: true });
        } else {
          myToast.notify_warning(payload?.api_remark || "Promotion failed.");
        }
      } catch (error) {
        console.error("[PromoteTransportStudent] promote error", error);
        myToast.notify_warning("Failed to promote transport students.");
      } finally {
        setSubmitting(false);
      }
    },
    [selectedStudents, sessionId, getStudentIds, loadStudents]
  );

  //Created By Cursor
  const handleShow = useCallback(() => {
    if (!selectedCount) {
      myToast.notify_error("Select Student !!!");
      return;
    }
    setIsOpen(true);
  }, [selectedCount]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  //Created By Cursor
  const handleSelectedClass = useCallback(
    (...args) => {
      handleClose();
      promoteStudents(...args);
    },
    [promoteStudents, handleClose]
  );

  //Created By Cursor
  const selectAll = useCallback(() => {
    const next = {};
    students.forEach((s) => {
      if (s.student_enrollment_no) next[s.student_enrollment_no] = s;
    });
    setSelectedMap(next);
  }, [students]);

  const clearSelection = useCallback(() => setSelectedMap({}), []);

  if (!loggedIn) return <Redirect to="/Login" />;

  const classSelected = Boolean(classId && sectionId && previousSession);
  const busy = loading || refreshing || submitting;

  const promoteButton = (
    <button
      type="button"
      className="rn-btn rn-btn--solid"
      onClick={handleShow}
      disabled={busy || selectedCount === 0}
    >
      <span className="rn-btn__icon" aria-hidden>
        <FaArrowUp />
      </span>
      <span className="rn-btn__text">
        <span className="rn-btn__title">
          {submitting
            ? "Promoting…"
            : selectedCount > 0
            ? `Promote (${selectedCount})`
            : "Promote"}
        </span>
        <span className="rn-btn__hint">
          {selectedCount > 0
            ? "Pick destination class & session"
            : "Select students first"}
        </span>
      </span>
    </button>
  );

  return (
    <div
      className="content payroll-page sm-page rn-page cc-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}>
                {" "}
                Promote Transport Student
              </span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBus />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Promote Transport Student</h1>
              <p className="sm-hero__sub">
                {classSectionName
                  ? `${classSectionName} · ${students.length} student${
                      students.length === 1 ? "" : "s"
                    }`
                  : "Select previous session & class to promote transport students"}
                {selectedCount > 0 ? ` · ${selectedCount} selected` : ""}
              </p>
            </div>
          </div>

          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <FaSearch className="rn-search-icon" aria-hidden />
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search students…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search students"
                disabled={!classSelected}
              />
            </div>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadStudents({ soft: true })}
              disabled={busy || !classSelected}
              aria-label="Refresh list"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="rn-content">
          {loadClass && previousSession ? (
            <div className="rn-class-wrap">
              <SelectSessionClass
                action={onClassSelect}
                selected_position={selectedClassPosition}
                session_id={previousSession.session_id}
                session_name={previousSession.session_name}
                session_status={previousSession.status}
                start_date={previousSession.start_date}
                end_date={previousSession.end_date}
                sessionChangePermission={false}
              />
            </div>
          ) : null}

          {!classSelected ? (
            <div className="sm-empty">
              <FaUserGraduate className="rn-empty-icon" aria-hidden />
              <p className="sm-empty__title">Select a class</p>
              <p className="sm-empty__hint">
                Choose class and section from previous session to load transport
                students
              </p>
            </div>
          ) : loading ? (
            <div className="rn-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              <div className="rn-toolbar rn-toolbar--top">
                <div className="rn-toolbar__status" aria-live="polite">
                  <span className="rn-status-chip rn-status-chip--students">
                    {students.length} student
                    {students.length === 1 ? "" : "s"}
                  </span>
                  {selectedCount > 0 ? (
                    <span className="rn-status-chip rn-status-chip--unsaved">
                      {selectedCount} selected
                    </span>
                  ) : null}
                </div>
                <div className="cc-toolbar-actions">
                  <button
                    type="button"
                    className="rn-btn rn-btn--ghost"
                    onClick={selectAll}
                    disabled={busy || students.length === 0}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="rn-btn rn-btn--ghost"
                    onClick={clearSelection}
                    disabled={busy || selectedCount === 0}
                  >
                    Clear
                  </button>
                  {promoteButton}
                </div>
              </div>

              {filteredStudents.length === 0 ? (
                <div className="sm-empty">
                  <p className="sm-empty__title">No students found</p>
                  <p className="sm-empty__hint">
                    {searchTerm
                      ? "Try a different search term."
                      : "No transport students in this class for the previous session."}
                  </p>
                </div>
              ) : (
                <div className="rn-list">
                  {filteredStudents.map((student, index) => (
                    <StudentPromoteRow
                      key={student.id || student.student_enrollment_no}
                      student={student}
                      selected={Boolean(
                        selectedMap[student.student_enrollment_no]
                      )}
                      index={index}
                      onToggle={toggleStudent}
                    />
                  ))}
                </div>
              )}

              {students.length > 0 ? (
                <div className="rn-toolbar rn-toolbar--bottom">
                  {promoteButton}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {isOpen ? (
        <MdlBatchShift
          actionClose={handleClose}
          actionSelectClass={handleSelectedClass}
        />
      ) : null}
    </div>
  );
}

export default PromoteTransportStudent;
